
import { useState, useEffect, useRef, useCallback } from "react";
import { useInView } from "react-intersection-observer";
import { motion, AnimatePresence } from "framer-motion";
import { PostCard } from "@/components/post/PostCard";
import { FeedSkeleton } from "./FeedSkeleton";
import { PullToRefresh } from "@/components/ui/PullToRefresh";
import { useToast } from "@/components/ui/ToastProvider";
import { cn } from "@/lib/utils/cn";
import { IconRefresh } from "@/components/ui/Icons";
import type { Post, SortOrder, TimeRange } from "@/types";

interface FeedContainerProps {
  userId: string | null;
  subscribedTopics: string[];
  topicId?: string;
  initialSort?: SortOrder;
}

const PAGE_SIZE = 20;

export function FeedContainer({
  userId,
  subscribedTopics,
  topicId,
  initialSort = "hot",
}: FeedContainerProps) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [sort, setSort] = useState<SortOrder>(initialSort);
  const [timeRange, setTimeRange] = useState<TimeRange>("day");
  const [newPostsAvailable, setNewPostsAvailable] = useState(false);
  const [latestPostTime, setLatestPostTime] = useState<string | null>(null);
  const seenIds = useRef(new Set<string>());
  const { error: showError } = useToast();

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: "200px",
  });

  const fetchPosts = useCallback(
    async (
      resetCursor = false,
      currentSort = sort,
      currentTimeRange = timeRange
    ) => {
      const isFirstLoad = resetCursor || posts.length === 0;
      if (isFirstLoad) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const params = new URLSearchParams({
          sort: currentSort,
          limit: PAGE_SIZE.toString(),
          ...(topicId && { topic_id: topicId }),
          ...(userId && { user_id: userId }),
          ...(currentTimeRange !== "day" && { time_range: currentTimeRange }),
          ...(!resetCursor && cursor ? { cursor } : {}),
        });

        const res = await fetch(`/api/feed?${params.toString()}`);

        if (!res.ok) throw new Error("Failed to fetch feed");

        const data = await res.json();

        if (resetCursor) {
          seenIds.current.clear();
        }

        const newPosts = data.posts.filter((p: Post) => {
          if (seenIds.current.has(p.id)) return false;
          seenIds.current.add(p.id);
          return true;
        });

        if (resetCursor) {
          setPosts(newPosts);
          if (newPosts.length > 0) {
            setLatestPostTime(newPosts[0].published_at);
          }
        } else {
          setPosts((prev) => [...prev, ...newPosts]);
        }

        setCursor(data.cursor);
        setHasMore(data.has_more);
        setNewPostsAvailable(false);
      } catch (err) {
        showError("Failed to load feed", "Please try again.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [sort, timeRange, cursor, topicId, userId, posts.length, showError]
  );

  // Initial load
  useEffect(() => {
    fetchPosts(true, sort, timeRange);
    seenIds.current.clear();
  }, [sort, timeRange, topicId]);

  // Infinite scroll
  useEffect(() => {
    if (inView && hasMore && !loadingMore && !loading) {
      fetchPosts(false);
    }
  }, [inView]);

  // Poll for new posts (when on hot/new sort)
  useEffect(() => {
    if (sort !== "hot" && sort !== "new") return;

    const interval = setInterval(async () => {
      if (!latestPostTime) return;

      try {
        const params = new URLSearchParams({
          sort: "new",
          limit: "1",
          after: latestPostTime,
          ...(topicId && { topic_id: topicId }),
        });

        const res = await fetch(`/api/feed?${params.toString()}`);
        if (!res.ok) return;

        const data = await res.json();
        if (data.posts.length > 0 && data.posts[0].id !== posts[0]?.id) {
          setNewPostsAvailable(true);
        }
      } catch {
        // Silent fail — polling is optional
      }
    }, 60_000); // Check every 60 seconds

    return () => clearInterval(interval);
  }, [latestPostTime, sort, topicId, posts]);

  const handleRefresh = useCallback(async () => {
    setCursor(null);
    await fetchPosts(true);
  }, [fetchPosts]);

  const handleSortChange = (newSort: SortOrder) => {
    setSort(newSort);
    setCursor(null);
    seenIds.current.clear();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLoadNewPosts = () => {
    setCursor(null);
    fetchPosts(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return <FeedSkeleton />;
  }

  if (!loading && posts.length === 0) {
    return <EmptyFeed sort={sort} hasSubscriptions={subscribedTopics.length > 0} />;
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-3">
        {/* New posts notification */}
        <AnimatePresence>
          {newPostsAvailable && (
            <motion.button
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={handleLoadNewPosts}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl bg-brand text-brand-foreground text-sm font-medium shadow-glow-sm hover:bg-brand-hover transition-colors"
            >
              <IconRefresh className="w-4 h-4" />
              New posts available — tap to refresh
            </motion.button>
          )}
        </AnimatePresence>

        {/* Posts */}
        {posts.map((post, index) => (
          <motion.div
            key={post.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.3,
              delay: Math.min(index * 0.04, 0.3),
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            <PostCard post={post} userId={userId} />
          </motion.div>
        ))}

        {/* Load more trigger */}
        <div ref={loadMoreRef} className="h-4" />

        {/* Loading more */}
        {loadingMore && (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <PostCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* End of feed */}
        {!hasMore && posts.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="end-of-feed"
          >
            <div className="w-10 h-10 rounded-2xl bg-muted flex items-center justify-center mb-3">
              <svg className="w-5 h-5 text-text-subtle" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-text-muted">You&apos;ve seen it all</p>
            <p className="text-xs text-text-subtle mt-1">
              Check back later for new posts
            </p>
          </motion.div>
        )}
      </div>
    </PullToRefresh>
  );
}

function EmptyFeed({
  sort,
  hasSubscriptions,
}: {
  sort: SortOrder;
  hasSubscriptions: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-20 text-center"
    >
      <div className="w-16 h-16 rounded-3xl bg-brand-muted flex items-center justify-center mb-4">
        <svg
          className="w-8 h-8 text-brand"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">
        {hasSubscriptions ? "Nothing here yet" : "Discover communities"}
      </h3>
      <p className="text-sm text-text-muted max-w-xs">
        {hasSubscriptions
          ? "Your followed topics don't have recent posts. Explore trending content instead."
          : "Follow topics to see personalized content in your feed."}
      </p>
    </motion.div>
  );
}

function PostCardSkeleton() {
  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <div className="skeleton w-8 h-8 rounded-full" />
        <div className="space-y-1.5 flex-1">
          <div className="skeleton h-3 w-32 rounded" />
          <div className="skeleton h-2.5 w-20 rounded" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="skeleton h-4 w-full rounded" />
        <div className="skeleton h-4 w-4/5 rounded" />
        <div className="skeleton h-3.5 w-3/5 rounded" />
      </div>
      <div className="flex gap-3">
        <div className="skeleton h-7 w-16 rounded-lg" />
        <div className="skeleton h-7 w-20 rounded-lg" />
        <div className="skeleton h-7 w-14 rounded-lg" />
      </div>
    </div>
  );
}
